import React, { useRef, useState, useCallback } from 'react';
import { useDateFormat, DateFormat } from '../../settings/TimePicker';

/**
 * Segmented date input — respects the user's date format setting (MM/DD/YYYY or DD/MM/YYYY).
 * Clicking on a segment pre-selects it for direct overtype.
 */

type Segment = 'month' | 'day' | 'year';

// Segment at string position 0-1 depends on format
function firstSeg(format: DateFormat): Segment { return format === 'MM/DD/YYYY' ? 'month' : 'day'; }
function secondSeg(format: DateFormat): Segment { return format === 'MM/DD/YYYY' ? 'day' : 'month'; }

function formatDate(iso: string, format: DateFormat): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return format === 'DD/MM/YYYY' ? `${d}/${m}/${y}` : `${m}/${d}/${y}`;
}

function parseDate(input: string, format: DateFormat): string | null {
  const clean = input.trim();
  const m1 = clean.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m1) {
    const a = parseInt(m1[1]), b = parseInt(m1[2]), yr = parseInt(m1[3]);
    const [mo, dy] = format === 'DD/MM/YYYY' ? [b, a] : [a, b];
    if (mo >= 1 && mo <= 12 && dy >= 1 && dy <= 31 && yr >= 2000 && yr <= 2100)
      return `${yr}-${String(mo).padStart(2, '0')}-${String(dy).padStart(2, '0')}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) return clean;
  return null;
}

function detectSegment(pos: number, format: DateFormat): Segment {
  if (pos <= 2) return firstSeg(format);
  if (pos <= 5) return secondSeg(format);
  return 'year';
}

function segmentRange(seg: Segment, format: DateFormat): [number, number] {
  const s1 = firstSeg(format), s2 = secondSeg(format);
  if (seg === s1) return [0, 2];
  if (seg === s2) return [3, 5];
  return [6, 10];
}

function segmentMax(seg: Segment): number { return seg === 'year' ? 4 : 2; }

function clampSegment(seg: Segment, n: number): number {
  if (seg === 'month') return Math.min(Math.max(n, 1), 12);
  if (seg === 'day') return Math.min(Math.max(n, 1), 31);
  return Math.min(Math.max(n, 2000), 2100);
}

function nextSegment(seg: Segment, format: DateFormat): Segment | null {
  if (seg === firstSeg(format)) return secondSeg(format);
  if (seg === secondSeg(format)) return 'year';
  return null;
}
function prevSegment(seg: Segment, format: DateFormat): Segment | null {
  if (seg === 'year') return secondSeg(format);
  if (seg === secondSeg(format)) return firstSeg(format);
  return null;
}

interface DateTextInputProps {
  value: string; // ISO YYYY-MM-DD
  onChange: (d: string) => void;
  onFocus?: () => void;
  className?: string;
}

export const DateTextInput: React.FC<DateTextInputProps> = ({ value, onChange, onFocus, className }) => {
  const dateFormat = useDateFormat();
  const inputRef = useRef<HTMLInputElement>(null);
  const [segment, setSegment] = useState<Segment | null>(null);
  const [buf, setBuf] = useState('');

  const parts = value ? value.split('-') : null;
  // Semantic parts
  const isoMM = parts ? parts[1] : 'MM';
  const isoDD = parts ? parts[2] : 'DD';
  const isoYYYY = parts ? parts[0] : 'YYYY';
  // Display parts in the right order for the format
  const displayP1 = parts ? (dateFormat === 'MM/DD/YYYY' ? parts[1] : parts[2]) : (dateFormat === 'MM/DD/YYYY' ? 'MM' : 'DD');
  const displayP2 = parts ? (dateFormat === 'MM/DD/YYYY' ? parts[2] : parts[1]) : (dateFormat === 'MM/DD/YYYY' ? 'DD' : 'MM');
  const displayYYYY = parts ? parts[0] : 'YYYY';
  const placeholder = dateFormat === 'MM/DD/YYYY' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';

  const display = value ? formatDate(value, dateFormat) : placeholder;

  const highlight = useCallback((seg: Segment) => {
    requestAnimationFrame(() => {
      const [s, e] = segmentRange(seg, dateFormat);
      inputRef.current?.setSelectionRange(s, e);
    });
  }, [dateFormat]);

  const selectSegment = useCallback((seg: Segment) => {
    setSegment(seg);
    setBuf('');
    highlight(seg);
  }, [highlight]);

  const commitBuf = useCallback((currentBuf: string, seg: Segment) => {
    if (!currentBuf || !value) return;
    const n = parseInt(currentBuf, 10);
    if (isNaN(n)) return;
    const clamped = clampSegment(seg, n);
    const [y, m, d] = value.split('-');
    let newIso: string;
    if (seg === 'month') {
      newIso = `${y}-${String(clamped).padStart(2, '0')}-${d}`;
    } else if (seg === 'day') {
      newIso = `${y}-${m}-${String(clamped).padStart(2, '0')}`;
    } else {
      newIso = `${String(clamped).padStart(4, '0')}-${m}-${d}`;
    }
    onChange(newIso);
    setBuf('');
  }, [onChange, value]);

  const handleClick = () => {
    inputRef.current?.focus();
    const pos = inputRef.current?.selectionStart ?? 0;
    selectSegment(detectSegment(pos, dateFormat));
  };

  const handleFocus = () => {
    if (!segment) selectSegment(firstSeg(dateFormat));
    onFocus?.();
  };

  const handleBlur = () => {
    if (segment && buf) commitBuf(buf, segment);
    setSegment(null);
    setBuf('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const seg = segment || firstSeg(dateFormat);

    if (e.key === 'Tab') {
      const nxt = nextSegment(seg, dateFormat);
      if (nxt) {
        e.preventDefault();
        commitBuf(buf, seg);
        selectSegment(nxt);
      } else {
        commitBuf(buf, seg);
        setSegment(null);
        setBuf('');
      }
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault(); e.stopPropagation();
      commitBuf(buf, seg);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault(); setBuf(''); inputRef.current?.blur(); return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault(); commitBuf(buf, seg);
      const prev = prevSegment(seg, dateFormat);
      if (prev) selectSegment(prev);
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault(); commitBuf(buf, seg);
      const nxt = nextSegment(seg, dateFormat);
      if (nxt) selectSegment(nxt);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!value) return;
      const [y, m, d] = value.split('-').map(Number);
      let newIso: string;
      if (seg === 'month') {
        const nm = m === 12 ? 1 : m + 1;
        newIso = `${y}-${String(nm).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      } else if (seg === 'day') {
        const nd = d + 1 > new Date(y, m, 0).getDate() ? 1 : d + 1;
        newIso = `${y}-${String(m).padStart(2, '0')}-${String(nd).padStart(2, '0')}`;
      } else {
        newIso = `${y + 1}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
      onChange(newIso);
      requestAnimationFrame(() => highlight(seg));
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!value) return;
      const [y, m, d] = value.split('-').map(Number);
      let newIso: string;
      if (seg === 'month') {
        const nm = m === 1 ? 12 : m - 1;
        newIso = `${y}-${String(nm).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      } else if (seg === 'day') {
        const nd = d - 1 < 1 ? new Date(y, m, 0).getDate() : d - 1;
        newIso = `${y}-${String(m).padStart(2, '0')}-${String(nd).padStart(2, '0')}`;
      } else {
        newIso = `${y - 1}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
      onChange(newIso);
      requestAnimationFrame(() => highlight(seg));
      return;
    }
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const newBuf = buf + e.key;
      setBuf(newBuf);
      if (newBuf.length >= segmentMax(seg)) {
        const n = parseInt(newBuf, 10);
        const clamped = clampSegment(seg, n);
        const [y, m, d] = (value || `${new Date().getFullYear()}-01-01`).split('-');
        let newIso: string;
        if (seg === 'month') {
          newIso = `${y}-${String(clamped).padStart(2, '0')}-${d}`;
        } else if (seg === 'day') {
          newIso = `${y}-${m}-${String(clamped).padStart(2, '0')}`;
        } else {
          if (newBuf.length < 4) { highlight(seg); return; }
          newIso = `${String(clamped).padStart(4, '0')}-${m}-${d}`;
        }
        onChange(newIso);
        setBuf('');
        const nxt = nextSegment(seg, dateFormat);
        if (nxt) requestAnimationFrame(() => selectSegment(nxt));
        else requestAnimationFrame(() => { inputRef.current?.blur(); setSegment(null); });
      } else {
        if (seg !== 'year' && newBuf.length === 1) {
          const threshold = seg === 'month' ? 1 : 3;
          if (parseInt(newBuf, 10) > threshold) {
            const clamped = clampSegment(seg, parseInt(newBuf, 10));
            const [y, m, d] = (value || `${new Date().getFullYear()}-01-01`).split('-');
            let newIso: string;
            if (seg === 'month') {
              newIso = `${y}-${String(clamped).padStart(2, '0')}-${d}`;
            } else {
              newIso = `${y}-${m}-${String(clamped).padStart(2, '0')}`;
            }
            onChange(newIso);
            setBuf('');
            const nxt = nextSegment(seg, dateFormat);
            if (nxt) requestAnimationFrame(() => selectSegment(nxt));
          } else {
            highlight(seg);
          }
        } else {
          highlight(seg);
        }
      }
      return;
    }
    if (e.key === 'Backspace') {
      e.preventDefault();
      setBuf(buf.slice(0, -1));
      return;
    }
    e.preventDefault();
  };

  // Build display — show buf in active segment
  let displayValue = display;
  if (buf && segment && value) {
    const maxLen = segmentMax(segment);
    const padded = buf.padEnd(maxLen, '_').slice(0, maxLen);
    const s1 = firstSeg(dateFormat), s2 = secondSeg(dateFormat);
    if (segment === s1) {
      displayValue = `${padded}/${displayP2}/${displayYYYY}`;
    } else if (segment === s2) {
      displayValue = `${displayP1}/${padded}/${displayYYYY}`;
    } else {
      displayValue = `${displayP1}/${displayP2}/${padded}`;
    }
  } else if (buf && segment && !value) {
    if (segment === firstSeg(dateFormat)) {
      const ph2 = dateFormat === 'MM/DD/YYYY' ? 'DD' : 'MM';
      displayValue = `${buf.padEnd(2, '_')}/${ph2}/YYYY`;
    }
  }

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={displayValue}
      placeholder={placeholder}
      onClick={handleClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onChange={() => {}}
      className={className ?? 'flex-1 min-w-0 px-2.5 py-1.5 bg-[var(--input-bg)] rounded-lg text-xs text-right text-[var(--content-primary)] placeholder-[var(--content-tertiary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]'}
      style={{ caretColor: 'transparent' }}
      data-no-drag
    />
  );
};
